import React, { forwardRef } from 'react';
import { Icon } from '../../Icon';
import { keyCode } from '../../../helpers';
import { useTooltip } from '../../../hooks';

/**
 * One button in a tab strip, on a page and in a form alike.
 *
 * @param {object} props
 * @param {string} props.activeTab - The tabid of the tab currently open.
 * @param {string} props.label - What the button reads.
 * @param {string} props.tabid - What identifies this tab, and what onClick is handed.
 * @param {number} [props.resultCount] - Shown beside the label when there is anything to count.
 * @param {function} props.onClick
 * @param {boolean} [props.disableTab] - Renders the button unselectable.
 * @param {object} [props.tooltip] - Shown on hover, in the shape useTooltip takes.
 * @param {boolean} [props.error] - Marks the tab as holding something that needs attention.
 * @param {React.Ref} ref
 * @returns {JSX.Element}
 */
const _Tab = (
  { activeTab, label, tabid, resultCount, onClick, disableTab, tooltip, error },
  ref
) => {
  const { showTooltip, hideTooltip } = useTooltip();

  const onClickTabItem = () => {
    if (!disableTab) {
      onClick(tabid);
    }
  };
  const handleKeyDown = e => {
    switch (e.keyCode) {
      case keyCode.ENTER:
      case keyCode.SPACE:
        onClickTabItem();
        break;

      default:
        break;
    }
  };

  const active = activeTab === tabid ? 'active' : '';

  return (
    <div
      className={
        'tab-list-item ' +
        active +
        (resultCount > 0 ? ' has-results' : '') +
        (disableTab ? ' disabled' : '')
      }
      role="tab"
      ref={ref}
      aria-selected={active === 'active'}
      tabIndex={0}
      onClick={onClickTabItem}
      onKeyDown={handleKeyDown}
      onMouseEnter={tooltip ? () => showTooltip(tooltip) : undefined}
      onMouseLeave={tooltip ? hideTooltip : undefined}
    >
      <span className="label">{label ? label : 'undefined'}</span>

      {resultCount > 0 && (
        <span className="label-result-count">{resultCount.toLocaleString()}</span>
      )}

      {/* The icon every validation message in the GUI uses, so a tab reads as holding a problem
          without having to be opened. Styled by the tab strip that asks for it. */}
      {error && <Icon name="failed-small" tooltip="Something on this tab needs attention" />}
    </div>
  );
};
export const Tab = forwardRef(_Tab);
